import { useMemo, useState } from "react";
import { AuthPageShell } from "../components/AuthPageShell";
import type { AuthorizationResult } from "../types";

function WarningIcon({ className }: { className: string }) {
  return (
    <svg className={`mx-auto mb-4 h-12 w-12 ${className}`} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
    </svg>
  );
}

interface ServiceErrorProps {
  message?: string;
}

export function ServiceError({ message }: ServiceErrorProps = {}) {
  return (
    <AuthPageShell>
      <WarningIcon className="text-amber-500" />
      <h2 className="text-xl font-bold text-gray-900 mb-2 text-center">Something Went Wrong</h2>
      <p className="text-sm text-gray-500 mb-6 text-center">
        {message || "An unexpected error occurred. Please try again."}
      </p>
      <button
        onClick={() => window.location.reload()}
        className="w-full rounded-lg bg-brand-green px-4 py-3 text-white font-semibold hover:bg-brand-green-dark transition-colors cursor-pointer"
      >
        Try Again
      </button>
    </AuthPageShell>
  );
}

export function AccessDenied() {
  return (
    <AuthPageShell>
      <WarningIcon className="text-red-400" />
      <h2 className="text-xl font-bold text-gray-900 mb-2 text-center">Access Denied</h2>
      <p className="text-sm text-gray-500 text-center">
        You do not have access to this application. If you believe this is an error, please contact your administrator or sales agent.
      </p>
    </AuthPageShell>
  );
}

export function InternalNoGroup() {
  return (
    <AuthPageShell>
      <svg className="mx-auto mb-4 h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <path d="M12 16v-4" />
        <path d="M12 8h.01" />
      </svg>
      <h2 className="text-xl font-bold text-gray-900 mb-2 text-center">Group URL Required</h2>
      <p className="text-sm text-gray-500 text-center">
        Internal users must access the intake form via a specific group URL. Please use the link from Salesforce to navigate to the correct group.
      </p>
    </AuthPageShell>
  );
}

export function InternalInvalidGroup() {
  return (
    <AuthPageShell>
      <WarningIcon className="text-red-400" />
      <h2 className="text-xl font-bold text-gray-900 mb-2 text-center">Group Not Found</h2>
      <p className="text-sm text-gray-500 text-center">
        The specified group could not be found. Please check that the link is correct and try again.
      </p>
    </AuthPageShell>
  );
}

interface GroupPickerProps {
  groups: AuthorizationResult["groups"];
  message?: string;
}

export function GroupPicker({ groups, message }: GroupPickerProps) {
  const [search, setSearch] = useState("");
  const sorted = useMemo(() => {
    const lowerSearch = search.toLowerCase();
    return groups
      .filter((g) => g.accountName.toLowerCase().includes(lowerSearch))
      .sort((a, b) => a.accountName.localeCompare(b.accountName));
  }, [groups, search]);
  const showSearch = groups.length >= 6;

  const defaultMessage = `You have access to ${groups.length} group${groups.length === 1 ? "" : "s"}. Select one to continue.`;

  return (
    <AuthPageShell>
      <h2 className="text-xl font-bold text-gray-900 mb-1 text-center">Select a Group</h2>
      <p className="text-sm text-gray-500 mb-6 text-center">
        {message || defaultMessage}
      </p>

      {showSearch && (
        <input
          type="text"
          aria-label="Search groups"
          placeholder="Search groups..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full mb-4 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand-green focus:outline-none focus:ring-1 focus:ring-brand-green"
        />
      )}

      <div className="max-h-80 overflow-y-auto space-y-2">
        {sorted.map((group) => (
          <a
            key={group.accountId}
            href={group.oppId
              ? `/intake?oppId=${encodeURIComponent(group.oppId)}`
              : `/intake?groupId=${encodeURIComponent(group.accountId)}`}
            className="flex items-center justify-between w-full rounded-lg border border-gray-200 px-4 py-3 text-left text-sm font-medium text-gray-900 hover:border-brand-green hover:bg-green-50 transition-colors"
          >
            <span>{group.accountName}</span>
            <svg className="h-4 w-4 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
            </svg>
          </a>
        ))}
        {sorted.length === 0 && (
          <p className="text-sm text-gray-400 text-center py-4">No groups match your search.</p>
        )}
      </div>
    </AuthPageShell>
  );
}
